import { create } from "zustand"
import { persist } from "zustand/middleware"
import {
  getTransactionKey,
  isActiveTransaction,
  isExpiredRecentProcess,
  isTerminalTransaction,
  normalizeTransactionType,
  TransactionProgress,
} from "features/process-monitor/types"

type ProcessMonitorState = {
  organisationId?: string
  processes: Record<string, TransactionProgress>
  isOpen: boolean
  focusedProcessKey?: string
  ensureOrganisation: (organisationId?: string) => void
  upsertProcess: (process: TransactionProgress) => void
  dismissProcess: (key: string) => void
  pruneExpiredProcesses: () => void
  setOpen: (isOpen: boolean) => void
  setFocusedProcess: (key?: string) => void
}

const MAX_SAVED_PROCESSES = 20

const normalizeProcesses = (
  processes: Record<string, TransactionProgress>
) =>
  Object.values(processes)
    // Version 4 collapsed every payroll action into the generic "payroll"
    // type. Those records cannot be safely assigned to prepare, refresh, or
    // submit after upgrading, so let Core recovery hydrate them again.
    .filter(
      (process) => normalizeTransactionType(process.type) !== "payroll"
    )
    .filter((process) => !isExpiredRecentProcess(process))
    .reduce<Record<string, TransactionProgress>>(
      (normalized, process) => {
        const normalizedProcess = {
          ...process,
          type: normalizeTransactionType(process.type),
          terminalAt: isTerminalTransaction(process.status)
            ? process.updatedAt || process.initiatedAt || process.terminalAt
            : undefined,
        }
        const key = getTransactionKey(
          normalizedProcess.type,
          normalizedProcess.id
        )
        const existing = normalized[key]

        normalized[key] = existing
          ? new Date(existing.updatedAt).getTime() >
            new Date(normalizedProcess.updatedAt).getTime()
            ? { ...normalizedProcess, ...existing }
            : { ...existing, ...normalizedProcess }
          : normalizedProcess

        return normalized
      },
      {}
    )

export const useProcessMonitorStore = create<ProcessMonitorState>()(
  persist(
    (set) => ({
      organisationId: undefined,
      processes: {},
      isOpen: false,
      focusedProcessKey: undefined,

      ensureOrganisation: (organisationId) =>
        set((state) =>
          state.organisationId === organisationId
            ? state
            : {
                organisationId,
                processes: {},
                isOpen: false,
                focusedProcessKey: undefined,
              }
        ),

      upsertProcess: (process) =>
        set((state) => {
          const normalizedProcess = {
            ...process,
            type: normalizeTransactionType(process.type),
          }
          const key = getTransactionKey(
            normalizedProcess.type,
            normalizedProcess.id
          )
          const optimisticKey = getTransactionKey(
            normalizedProcess.type,
            normalizedProcess.type
          )
          const aliasKeys = Object.entries(state.processes)
            .filter(
              ([storedKey, candidate]) =>
                storedKey !== key &&
                getTransactionKey(candidate.type, candidate.id) === key
            )
            .map(([storedKey]) => storedKey)
          const aliasProcess = aliasKeys
            .map((aliasKey) => state.processes[aliasKey])
            .sort(
              (first, second) =>
                new Date(second.updatedAt).getTime() -
                new Date(first.updatedAt).getTime()
            )[0]
          const optimisticProcess =
            optimisticKey !== key ? state.processes[optimisticKey] : undefined
          const existingProcess =
            state.processes[key] || aliasProcess || optimisticProcess
          const terminalAt = isTerminalTransaction(normalizedProcess.status)
            ? isTerminalTransaction(existingProcess?.status)
              ? existingProcess?.terminalAt || existingProcess?.updatedAt
              : existingProcess &&
                isActiveTransaction(existingProcess.status)
              ? new Date().toISOString()
              : normalizedProcess.updatedAt ||
                normalizedProcess.initiatedAt ||
                new Date().toISOString()
            : undefined
          const processes = {
            ...state.processes,
            [key]: {
              ...aliasProcess,
              ...optimisticProcess,
              ...state.processes[key],
              ...normalizedProcess,
              terminalAt,
              progress: Math.min(
                Math.max(Number(normalizedProcess.progress) || 0, 0),
                100
              ),
            },
          }

          aliasKeys.forEach((aliasKey) => delete processes[aliasKey])
          if (optimisticProcess) delete processes[optimisticKey]

          const savedEntries = Object.entries(processes)
            .filter(([, savedProcess]) => !isExpiredRecentProcess(savedProcess))
            .sort(
              ([, first], [, second]) =>
                new Date(second.updatedAt).getTime() -
                new Date(first.updatedAt).getTime()
            )
            .slice(0, MAX_SAVED_PROCESSES)

          return {
            processes: Object.fromEntries(savedEntries),
            focusedProcessKey:
              state.focusedProcessKey === optimisticKey ||
              aliasKeys.includes(state.focusedProcessKey || "")
                ? key
                : state.focusedProcessKey,
          }
        }),

      dismissProcess: (key) =>
        set((state) => {
          const processes = { ...state.processes }
          delete processes[key]
          return { processes }
        }),

      pruneExpiredProcesses: () =>
        set((state) => ({
          processes: Object.fromEntries(
            Object.entries(state.processes).filter(
              ([, process]) => !isExpiredRecentProcess(process)
            )
          ),
        })),

      setOpen: (isOpen) => set({ isOpen }),
      setFocusedProcess: (focusedProcessKey) => set({ focusedProcessKey }),
    }),
    {
      name: "paidhr-process-monitor",
      version: 5,
      migrate: (persistedState) => {
        const state = persistedState as Pick<
          ProcessMonitorState,
          "organisationId" | "processes"
        >

        return {
          ...state,
          processes: normalizeProcesses(state.processes || {}),
        } as ProcessMonitorState
      },
      partialize: ({ organisationId, processes }) => ({
        organisationId,
        processes,
      }),
    }
  )
)
